import React, { useEffect, useCallback } from "react";

/**
 * ImageViewerModal - Reusable modal for viewing images full-screen
 *
 * Props:
 * - imageUrl: URL or data URI of the image to display (required to show modal)
 * - onClose: Callback when modal is closed
 * - images: Optional array of image URLs for carousel mode
 * - currentIndex: Current index in the images array
 * - onNavigate: Callback with new index when navigating (prev/next)
 */
const ImageViewerModal = ({
  imageUrl,
  onClose,
  images,
  currentIndex,
  onNavigate,
}) => {
  const isCarousel = images && images.length > 1 && onNavigate;

  const handlePrev = useCallback(() => {
    if (!isCarousel) return;
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    onNavigate(newIndex);
  }, [isCarousel, currentIndex, images?.length, onNavigate]);

  const handleNext = useCallback(() => {
    if (!isCarousel) return;
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    onNavigate(newIndex);
  }, [isCarousel, currentIndex, images?.length, onNavigate]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!imageUrl) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft" && isCarousel) {
        handlePrev();
      } else if (e.key === "ArrowRight" && isCarousel) {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [imageUrl, isCarousel, handlePrev, handleNext, onClose]);

  if (!imageUrl) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.9)",
          zIndex: 2000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          animation: "imageViewerFadeIn 0.2s ease-out",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            color: "white",
            fontSize: "2rem",
            width: "3rem",
            height: "3rem",
            borderRadius: "50%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
          title="Close (ESC)"
        >
          ✕
        </button>

        {/* Previous button */}
        {isCarousel && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            style={{
              position: "absolute",
              left: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "white",
              fontSize: "2rem",
              width: "3rem",
              height: "3rem",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
            }}
            title="Previous (←)"
          >
            ‹
          </button>
        )}

        {/* Next button */}
        {isCarousel && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            style={{
              position: "absolute",
              right: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "white",
              fontSize: "2rem",
              width: "3rem",
              height: "3rem",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
            }}
            title="Next (→)"
          >
            ›
          </button>
        )}

        {/* Image counter */}
        {isCarousel && (
          <div
            style={{
              position: "absolute",
              bottom: "1rem",
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "1rem",
              fontSize: "0.875rem",
            }}
          >
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {/* Full-size Image */}
        <img
          src={imageUrl}
          alt="Full size"
          style={{
            maxWidth: "95vw",
            maxHeight: "95vh",
            objectFit: "contain",
            borderRadius: "4px",
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <style>{`
        @keyframes imageViewerFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ImageViewerModal;
